import React, { useState, useEffect } from 'react';
import {
  DollarSign, Clock, Users, BookOpen, TrendingDown, TrendingUp,
  BarChart3, CheckCircle2, XCircle, Loader2, AlertCircle, Brain
} from 'lucide-react';
import { toast } from 'sonner';
import OperationsLayout, { PageHeader } from './OperationsLayout';
import { getExecutiveSummary, getDelta, getKnowledgeMetrics, getBaseline, setBaseline } from '@/lib/operationsApi';
import { Button } from '@/components/ui/button';

export default function ExecutiveDashboard() {
  const [exec, setExec]       = useState(null);
  const [delta, setDelta]     = useState(null);
  const [knowledge, setKnowledge] = useState(null);
  const [baseline, setBase]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [settingBaseline, setSettingBaseline] = useState(false);
  const [deltaError, setDeltaError] = useState(null);

  useEffect(() => {
    Promise.all([
      getExecutiveSummary(),
      getKnowledgeMetrics(),
      getBaseline(),
      getDelta().catch(e => { setDeltaError(e.message); return null; }),
    ])
      .then(([e, k, b, d]) => { setExec(e); setKnowledge(k); setBase(b); setDelta(d); })
      .catch(e => toast.error("Failed to load: " + e.message))
      .finally(() => setLoading(false));
  }, []);

  const handleSetBaseline = async () => {
    setSettingBaseline(true);
    try {
      await setBaseline(new Date().toISOString());
      toast.success("Baseline date set to today.");
      setDeltaError(null);
      const [b, d] = await Promise.all([getBaseline(), getDelta().catch(() => null)]);
      setBase(b); setDelta(d);
    } catch (e) { toast.error(e.message); }
    finally { setSettingBaseline(false); }
  };

  const hm = exec?.headline_metrics || {};
  const k  = knowledge || {};
  const targets = delta?.pilot_targets_met || {};

  // Derived business value estimates
  const avgMttrBefore = delta?.before?.avg_mttr_minutes;
  const avgMttrAfter  = delta?.after?.avg_mttr_minutes;
  const mttrSavedMin  = (avgMttrBefore && avgMttrAfter) ? Math.max(0, avgMttrBefore - avgMttrAfter) : null;
  const annualIncidents = (hm.incidents_resolved ?? 0) * (52 / 6);  // extrapolate 6-week pilot to annual
  const annualHoursSaved = mttrSavedMin ? Math.round((annualIncidents * mttrSavedMin) / 60) : null;
  const annualSavingsEst = annualHoursSaved ? Math.round(annualHoursSaved * 85) : null; // $85/hr blended rate
  const smeHoursSaved = hm.sme_hours_saved_estimate ?? 0;

  return (
    <OperationsLayout>
      <PageHeader
        title="Executive Dashboard"
        subtitle="6-week pilot outcome measurement — business impact report"
      />

      <div className="p-6 space-y-8">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* Baseline warning */}
            {baseline && !baseline.baseline_established && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-3 flex items-center justify-between gap-4">
                <div>
                  <div className="text-sm font-medium text-amber-800 dark:text-amber-300">Baseline not set</div>
                  <div className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                    Set today as the go-live date to begin measuring before/after improvement.
                  </div>
                </div>
                <Button size="sm" onClick={handleSetBaseline} disabled={settingBaseline}>
                  {settingBaseline && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
                  Set Baseline Today
                </Button>
              </div>
            )}

            {/* Business narrative */}
            {exec?.business_narrative && (
              <div className="bg-primary/5 border border-primary/20 rounded-lg px-5 py-4">
                <div className="text-xs font-semibold text-primary uppercase tracking-wide mb-2">
                  Business Impact Summary
                </div>
                <p className="text-base text-foreground leading-relaxed font-medium">{exec.business_narrative}</p>
              </div>
            )}

            {/* ── Section 1: Business Value ── */}
            <Section
              icon={DollarSign}
              title="Business Value"
              subtitle="Financial and workforce impact"
              color="green"
            >
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <ExecCard
                  label="Estimated Annual Savings"
                  value={annualSavingsEst != null ? `$${annualSavingsEst.toLocaleString()}` : "—"}
                  sub="based on MTTR improvement × incidents/year"
                  color="green"
                  note={annualSavingsEst == null ? "Set baseline to calculate" : undefined}
                />
                <ExecCard
                  label="Engineer Hours Recovered"
                  value={annualHoursSaved != null ? `~${annualHoursSaved.toLocaleString()}h/yr` : "—"}
                  sub="hours saved from faster resolution"
                  color="green"
                />
                <ExecCard
                  label="SME Hours Saved"
                  value={smeHoursSaved > 0 ? `~${smeHoursSaved}h` : "—"}
                  sub="avoided L2/SME escalations"
                  color="green"
                />
                <ExecCard
                  label="Incidents Resolved"
                  value={hm.incidents_resolved ?? "—"}
                  sub="during pilot period"
                  color="default"
                />
              </div>
            </Section>

            {/* ── Section 2: Operational Value ── */}
            <Section
              icon={BarChart3}
              title="Operational Value"
              subtitle="Before vs. after QueryVerse deployment"
              color="blue"
            >
              {deltaError && !delta ? (
                <div className="text-sm text-muted-foreground px-1">
                  {deltaError} — set a baseline date to see before/after comparison.
                </div>
              ) : delta ? (
                <div className="bg-card border border-border rounded-lg overflow-hidden">
                  <div className="grid grid-cols-3 divide-x divide-border">
                    <DeltaCol label="Before QueryVerse" kpis={delta.before} />
                    <DeltaCol label="After QueryVerse" kpis={delta.after} isAfter />
                    <div className="px-4 py-4">
                      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Improvement</div>
                      <DeltaRow label="MTTR" delta={delta.delta?.avg_mttr_minutes} target={25} />
                      <DeltaRow label="Escalation rate" delta={delta.delta?.escalation_pct} target={20} />
                      <DeltaRow label="First-time fix" delta={delta.delta?.first_time_fix_pct} target={15} isPositive />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <ExecCard label="Avg Resolution Time" value={hm.avg_resolution_time_minutes != null ? `${hm.avg_resolution_time_minutes}m` : "—"} sub="mean time to resolve" />
                  <ExecCard label="Escalations Avoided" value={hm.escalations_avoided_estimate ?? "—"} sub="vs. baseline rate" color="green" />
                  <ExecCard label="Knowledge Reuse" value={hm.knowledge_reuse_count ?? "—"} sub="times KB was applied" color="blue" />
                  <ExecCard label="First-Time Fix" value={delta?.after?.first_time_fix_pct != null ? `${delta.after.first_time_fix_pct}%` : "—"} sub="resolved without escalation" />
                </div>
              )}

              {/* Pilot targets */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                <TargetCard label="MTTR Reduction" target="≥ 25%" met={targets.mttr} note="Mean time to resolve" />
                <TargetCard label="Escalation Reduction" target="≥ 20%" met={targets.escalation} note="L2/L3 escalation rate" />
                <TargetCard label="First-Time Fix" target="≥ 15% improvement" met={targets.first_time_fix} note="Resolved without escalation" />
              </div>
            </Section>

            {/* ── Section 3: Knowledge Value ── */}
            <Section
              icon={Brain}
              title="Knowledge Value"
              subtitle="Organisational intelligence captured and reused"
              color="purple"
            >
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <ExecCard
                  label="Verified Knowledge"
                  value={k.verified_incidents ?? "—"}
                  sub="SME-approved articles"
                  color="purple"
                />
                <ExecCard
                  label="Promotion Rate"
                  value={k.promotion_rate_pct != null ? `${k.promotion_rate_pct}%` : "—"}
                  sub="incidents → knowledge"
                />
                <ExecCard
                  label="Knowledge Reuse"
                  value={hm.knowledge_reuse_count ?? "—"}
                  sub="times knowledge was applied"
                  color="purple"
                />
                <ExecCard
                  label="RAG Chunks"
                  value={k.incident_verified_chunks ?? "—"}
                  sub="verified knowledge in AI context"
                />
              </div>
              {k.knowledge_promotions && (
                <div className="flex gap-4 flex-wrap text-xs text-muted-foreground mt-2 px-1">
                  {Object.entries(k.knowledge_promotions).map(([s, count]) => (
                    <span key={s}>{s.replace(/_/g, " ")}: <strong className="text-foreground">{count}</strong></span>
                  ))}
                </div>
              )}
              <div className="bg-muted/30 rounded-lg px-4 py-3 mt-2 text-sm text-muted-foreground leading-relaxed">
                <strong className="text-foreground">Digital Memory:</strong>{" "}
                Every incident, every engineer action, every AI suggestion, every SME correction is preserved.
                This knowledge does not leave when engineers do.
              </div>
            </Section>
          </>
        )}
      </div>
    </OperationsLayout>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function Section({ icon: Icon, title, subtitle, color, children }) {
  const colors = {
    green:  "text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800",
    blue:   "text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800",
    purple: "text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800",
  };
  const iconColor = {
    green: "text-green-600 dark:text-green-400",
    blue: "text-blue-600 dark:text-blue-400",
    purple: "text-purple-600 dark:text-purple-400",
  };
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className={`p-1.5 rounded-md border ${colors[color]}`}>
          <Icon className={`w-4 h-4 ${iconColor[color]}`} />
        </div>
        <div>
          <div className="text-sm font-semibold text-foreground">{title}</div>
          <div className="text-xs text-muted-foreground">{subtitle}</div>
        </div>
      </div>
      {children}
    </div>
  );
}

function ExecCard({ label, value, sub, color = "default", note }) {
  const colors = {
    default: "text-foreground",
    green:   "text-green-600 dark:text-green-400",
    blue:    "text-blue-600 dark:text-blue-400",
    purple:  "text-purple-600 dark:text-purple-400",
  };
  return (
    <div className="bg-card border border-border rounded-lg px-4 py-4">
      <div className="text-xs text-muted-foreground mb-2">{label}</div>
      <div className={`text-2xl font-bold ${colors[color]}`}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
      {note && <div className="text-xs text-amber-600 dark:text-amber-400 mt-1">{note}</div>}
    </div>
  );
}

function DeltaCol({ label, kpis, isAfter }) {
  if (!kpis) return <div className="px-4 py-4 text-xs text-muted-foreground">No data</div>;
  return (
    <div className="px-4 py-4">
      <div className={`text-xs font-semibold uppercase tracking-wide mb-3 ${isAfter ? "text-green-600 dark:text-green-400" : "text-muted-foreground"}`}>
        {label}
      </div>
      <div className="space-y-2.5">
        <Kv label="Incidents" value={kpis.incident_count} />
        <Kv label="Avg MTTR" value={kpis.avg_mttr_minutes != null ? `${kpis.avg_mttr_minutes}m` : "—"} />
        <Kv label="Escalation" value={kpis.escalation_pct != null ? `${kpis.escalation_pct}%` : "—"} />
        <Kv label="First-time fix" value={kpis.first_time_fix_pct != null ? `${kpis.first_time_fix_pct}%` : "—"} />
      </div>
    </div>
  );
}

function Kv({ label, value }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-sm font-medium text-foreground">{value ?? "—"}</div>
    </div>
  );
}

function DeltaRow({ label, delta, target, isPositive }) {
  const achieved = delta != null && delta >= target;
  return (
    <div className="mb-2.5">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="flex items-center gap-1">
        {delta != null ? (
          <>
            {achieved
              ? <TrendingDown className="w-3 h-3 text-green-500" />
              : <TrendingUp className="w-3 h-3 text-muted-foreground" />
            }
            <span className={`text-sm font-medium ${achieved ? "text-green-600 dark:text-green-400" : "text-foreground"}`}>
              {delta > 0 ? "+" : ""}{delta}%
            </span>
            <span className="text-xs text-muted-foreground">target: {target}%</span>
          </>
        ) : <span className="text-xs text-muted-foreground">Insufficient data</span>}
      </div>
    </div>
  );
}

function TargetCard({ label, target, met, note }) {
  return (
    <div className={`rounded-lg border px-4 py-3 ${met ? "border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20" : "border-border bg-muted/20"}`}>
      <div className="flex items-center gap-2 mb-1">
        {met === true
          ? <CheckCircle2 className="w-4 h-4 text-green-500" />
          : met === false
            ? <XCircle className="w-4 h-4 text-muted-foreground" />
            : <AlertCircle className="w-4 h-4 text-amber-500" />}
        <span className="text-sm font-medium">{label}</span>
      </div>
      <div className={`text-lg font-bold ${met ? "text-green-600 dark:text-green-400" : "text-muted-foreground"}`}>{target}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{note}</div>
    </div>
  );
}
